import axios from 'axios'
import { errorMessage } from './api'

/**
 * The admin panel only ever talks to backend-nest — admin-lessons and
 * admin-attempts have no FastAPI equivalent and never will. It gets its own
 * env var rather than sharing VITE_API_BASE_URL with api.js, so pointing the
 * admin panel at /nest/api/v1 in production can't also repoint the consumer
 * app's own API calls at a backend that isn't at parity yet.
 */
const BASE_URL = import.meta.env.VITE_ADMIN_API_BASE_URL ?? '/nest/api/v1'
const KEY_STORE = 'tooti.admin_key'
const ACCESS_STORE = 'tooti.admin_access'
const REFRESH_STORE = 'tooti.admin_refresh'

export const adminKeyStore = {
  get() { return sessionStorage.getItem(KEY_STORE) },
  set(k) { sessionStorage.setItem(KEY_STORE, k) },
  clear() { sessionStorage.removeItem(KEY_STORE) },
}

/**
 * A named admin's own phone/OTP session — kept apart from adminKeyStore
 * (the shared static key) and from api.js's tokenStore (the consumer app's
 * own login), even though all three ultimately talk to the same backend-nest
 * auth. Sharing storage with either would mean logging into the admin panel
 * silently signs the browser into the consumer app too, or vice versa.
 */
export const adminSessionStore = {
  get access() { return sessionStorage.getItem(ACCESS_STORE) },
  get refresh() { return sessionStorage.getItem(REFRESH_STORE) },
  set({ accessToken, refreshToken }) {
    sessionStorage.setItem(ACCESS_STORE, accessToken)
    sessionStorage.setItem(REFRESH_STORE, refreshToken)
  },
  clear() {
    sessionStorage.removeItem(ACCESS_STORE)
    sessionStorage.removeItem(REFRESH_STORE)
  },
}

const adminApi = axios.create({
  baseURL: BASE_URL,
  timeout: 60_000,
  headers: { Accept: 'application/json' },
})

adminApi.interceptors.request.use((config) => {
  const access = adminSessionStore.access
  const key = adminKeyStore.get()
  if (access) config.headers.Authorization = `Bearer ${access}`
  else if (key) config.headers['X-API-Key'] = key
  return config
})

/** In-flight refresh shared by every request that hits a 401 at once (see api.js's twin). */
let refreshPromise = null

async function refreshAdminSession() {
  const refreshToken = adminSessionStore.refresh
  if (!refreshToken) throw new Error('No refresh token')
  // A bare axios call: going through adminApi would recurse into this same interceptor.
  const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken })
  adminSessionStore.set(data)
  return data.accessToken
}

adminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error
    // Sign-in calls answer 401 for a reason of their own and must not trigger a refresh;
    // changing the password is the one /auth/ call made *with* a session, which may need one.
    const isAuthCall = config?.url?.includes('/auth/') && !config.url.includes('admin-change-password')
    if (response?.status !== 401 || isAuthCall || config?._retried || !adminSessionStore.access) {
      return Promise.reject(error)
    }
    config._retried = true
    try {
      refreshPromise = refreshPromise ?? refreshAdminSession()
      const access = await refreshPromise
      config.headers.Authorization = `Bearer ${access}`
      return adminApi(config)
    } catch (refreshError) {
      adminSessionStore.clear()
      return Promise.reject(refreshError)
    } finally {
      refreshPromise = null
    }
  },
)

/**
 * The sign-in calls give up sooner than the panel's shared 60 s (tooti#256).
 * The slowest honest answer is an SMS that falls through both relay paths,
 * 10 s each (CARRIER_TIMEOUT_MS in backend-nest's sms.service.ts); past
 * that, waiting longer only delays the "could not reach the server" message.
 */
const OTP_TIMEOUT_MS = 30_000

export async function otpRequest(phone) {
  const { data } = await adminApi.post('/auth/otp-request', { phone }, { timeout: OTP_TIMEOUT_MS })
  return data
}

/** Verifies the code and, only for an admin-role account, stores the session. */
export async function otpVerify(phone, code) {
  const { data } = await adminApi.post('/auth/otp-verify', { phone, code }, { timeout: OTP_TIMEOUT_MS })
  if (data.user?.role !== 'admin') {
    throw Object.assign(new Error('این شماره دسترسی پنل ادمین را ندارد.'), { code: 'not_admin' })
  }
  adminSessionStore.set(data)
  return data
}

/**
 * Username + password (`auth_adminLogin`): the same token pair as the phone
 * path, kept in the same `adminSessionStore`. Answers the uniform 401 for every
 * refusal, so the caller shows one sentence (`passwordLoginError`).
 */
export async function adminLogin(username, password) {
  const { data } = await adminApi.post('/auth/admin-login', { username, password }, { timeout: OTP_TIMEOUT_MS })
  if (data.user?.role !== 'admin') {
    throw Object.assign(new Error('این حساب دسترسی پنل ادمین را ندارد.'), { code: 'not_admin' })
  }
  adminSessionStore.set(data)
  return data
}

/**
 * The signed-in admin's own password change (`auth_adminChangePassword`). The
 * server revokes every refresh token and answers with a fresh pair for this
 * session, which replaces the stored one.
 */
export async function adminChangePassword(currentPassword, newPassword) {
  const { data } = await adminApi.post('/auth/admin-change-password', { currentPassword, newPassword }, { timeout: OTP_TIMEOUT_MS })
  adminSessionStore.set(data)
  return data
}

export async function listLessons() {
  const { data } = await adminApi.get('/admin-lessons/list')
  return data.items
}

export async function createLesson(dto) {
  const { data } = await adminApi.post('/admin-lessons/create', dto)
  return data
}

/** One lesson, including auto-draft processing state — what the review flow below polls. */
export async function getLessonDetail(lessonId) {
  const { data } = await adminApi.get(`/admin-lessons/detail/${lessonId}`)
  return data
}

/**
 * form: { titleEn, titleFa, slug, level, description?, displayOrder?, file }
 * `file` is a video or a stand-alone voice recording — the pipeline decides
 * which from its mimetype. Returns immediately with processingStatus
 * "processing"; the caller polls getLessonDetail until it changes.
 */
export async function autoDraftLesson(form) {
  const data2 = new FormData()
  for (const key of ['titleEn', 'titleFa', 'slug', 'level', 'description', 'displayOrder']) {
    if (form[key] !== undefined && form[key] !== '') data2.append(key, form[key])
  }
  data2.append('file', form.file)
  const { data } = await adminApi.post('/admin-lessons/auto-draft', data2)
  return data
}

/** sentences: [{ draftIndex, textEn, translationFa, wordNotes? }] — the reviewed, final order. */
export async function confirmDraft(lessonId, sentences) {
  const { data } = await adminApi.post('/admin-lessons/confirm-draft', { lessonId, sentences })
  return data
}

export async function retryDraft(lessonId) {
  const { data } = await adminApi.post('/admin-lessons/retry-draft', { lessonId })
  return data
}

/**
 * patch: { titleEn?, titleFa?, description?, sentences?: [{ index, textEn?, translationFa?,
 * wordNotes?: { index, note }[] }] }
 *
 * A sentence's wordNotes, like its textEn/translationFa, is a per-sentence
 * field: omitted leaves the existing notes untouched, given (even `[]`)
 * replaces them wholesale. `pages/Admin.jsx` builds this shape (via
 * `lib/wordNotes.js`'s `sanitizeWordNotes`) before calling createLesson/
 * updateLesson — this file only forwards whatever body it is given.
 */
export async function updateLesson(lessonId, patch) {
  const { data } = await adminApi.post('/admin-lessons/update', { lessonId, ...patch })
  return data
}

/**
 * A sentence's reference audio — for a one-sentence lesson, the alternative
 * to a video when there is only a voice recording. `onProgress` (optional)
 * gets 0–1 as the bytes go up.
 */
export async function uploadAudio(lessonId, index, file, onProgress) {
  const form = new FormData()
  form.append('lessonId', lessonId)
  form.append('index', String(index))
  form.append('file', file)
  const { data } = await adminApi.post('/admin-lessons/upload-audio', form, {
    onUploadProgress: onProgress ? e => { if (e.total) onProgress(e.loaded / e.total) } : undefined,
  })
  return data
}

/**
 * A one-sentence lesson's reference audio is taken from this video's sound
 * track on the server, so this is the lesson's only media upload.
 * `onProgress` (optional) gets 0–1 as the bytes go up.
 */
export async function uploadVideo(lessonId, file, onProgress) {
  const form = new FormData()
  form.append('lessonId', lessonId)
  form.append('file', file)
  const { data } = await adminApi.post('/admin-lessons/upload-video', form, {
    onUploadProgress: onProgress ? e => { if (e.total) onProgress(e.loaded / e.total) } : undefined,
  })
  return data
}

export async function publishLesson(lessonId) {
  const { data } = await adminApi.post('/admin-lessons/publish', { lessonId })
  return data
}

export async function archiveLesson(lessonId) {
  const { data } = await adminApi.post('/admin-lessons/archive', { lessonId })
  return data
}

/** scheduledAt: ISO string or null to clear */
export async function scheduleLesson(lessonId, scheduledAt) {
  const { data } = await adminApi.post('/admin-lessons/schedule', { lessonId, scheduledAt })
  return data
}

/**
 * filters: { lessonId?, userId?, status?, limit? } — newest first, default
 * limit 100. `status` is one of `pending` · `analyzing` · `scored` ·
 * `failed` · `queued`; `queued` is the Plan B review queue (V9-b), which is
 * what the «صف بررسی» tab lists.
 */
export async function listAttempts(filters = {}) {
  const { data } = await adminApi.get('/admin-attempts/list', { params: filters })
  return data.items
}

/**
 * V9-p — one take's recording, for the review queue's player. Unlike an
 * onboarding sample's public URL this route is guarded (bearer token or the
 * shared admin key), so the bytes have to come through adminApi and the
 * caller turns the blob into an object URL — a plain `<audio src>` would
 * fetch it unauthenticated and get a 401. A row whose `userAudioUrl` is null
 * has nothing to fetch (retention dropped it, or the upload never finished).
 * 404 for no such attempt or no recording, 502 when object storage is down.
 */
export async function fetchAttemptAudio(attemptId) {
  const { data } = await adminApi.get(`/admin-attempts/audio/${attemptId}`, { responseType: 'blob' })
  return data
}

/**
 * V9-p — score a queued or failed take by hand. `score` is the overall, a
 * whole number 0–100, and is written as every sub-score: a manual score has
 * no word-by-word detail, which the panel says out loud next to the form.
 * XP, streak, coins and the learner's result notification go through the
 * same path as an Azure score, credited to the day the take was recorded;
 * `note` (≤ 500 characters, optional) is kept as the reason on the
 * `admin_audit_log` row. JWT-signed-in admins only — the shared admin key
 * gets 403 `admin_jwt_required`, because an audit row needs a person.
 * Refused with 422 `attempt_already_scored` (a score is credited once) or
 * 422 `attempt_not_scorable` (still mid-submit, or the requeue job has it at
 * this moment — retryable). Returns the updated attempt.
 */
export async function scoreAttempt(attemptId, score, note) {
  const body = { attemptId, score }
  if (note) body.note = note
  const { data } = await adminApi.post('/admin-attempts/score', body)
  return data
}

/**
 * V9-p — put a queued or failed take back at the front of the Plan B queue:
 * `queued`, due now, with a fresh try budget. The attempts-requeue job picks
 * it up on its next tick — but only once `planB.enabled` is off, since while
 * that switch is on the job leaves the queue alone. Refused with 422
 * `attempt_already_scored` / `attempt_not_scorable`, 404 for no such
 * attempt. Returns the updated attempt.
 */
export async function reanalyzeAttempt(attemptId) {
  const { data } = await adminApi.post('/admin-attempts/reanalyze', { attemptId })
  return data
}

/**
 * dto: { title, body } — one push to every learner at once, e.g. "a new
 * app version is out". Writes an inbox row for everyone and pushes every
 * live device regardless of the five channel switches (none of them fit
 * a message that isn't about any one learner). Returns { recipientCount,
 * pushedCount }.
 */
export async function broadcastAnnouncement(dto) {
  const { data } = await adminApi.post('/admin-notifications/broadcast', dto)
  return data
}

/**
 * How many learners a broadcast would reach right now — `{ recipientCount }`.
 * Read-only; the panel asks it before sending, so its confirmation can say
 * how many people the message goes to.
 */
export async function broadcastAudience() {
  const { data } = await adminApi.get('/admin-notifications/audience')
  return data
}

/**
 * Every registered runtime setting (X5): `{ key, description, sensitive,
 * default, value, updatedAt, updatedById }` per row — `value` is the default
 * until an admin overrides it.
 */
export async function listSettings() {
  const { data } = await adminApi.get('/admin-settings/detail')
  return data.items
}

/**
 * Override one setting. `value`'s shape depends on the key's registered type;
 * the backend validates it and answers 404 `unknown_setting_key` / 422
 * `invalid_setting_value`. Returns the stored value.
 */
export async function updateSetting(key, value) {
  const { data } = await adminApi.post('/admin-settings/update', { key, value })
  return data.value
}

/**
 * S3-p — learners, for the «کاربران» tab. filters: { q?, status?, limit?,
 * cursor? } → `{ items, nextCursor }`, newest signup first, ≤ 200 a page.
 * Every row carries `phoneMasked` only (`+98912***4567`); the full number
 * exists solely in getUser's answer, so a list can never show it.
 */
export async function listUsers(filters = {}) {
  const { data } = await adminApi.get('/admin-users/list', { params: filters })
  return data
}

/** One learner in full — profile, XP/streak, attempt counts, last activity, the admin audit trail (≤ 20). 404 `user_not_found`. */
export async function getUser(userId) {
  const { data } = await adminApi.get(`/admin-users/detail/${userId}`)
  return data
}

/**
 * Activate or deactivate a learner with a reason (3–500 characters, written
 * to `admin_audit_log`). JWT-signed-in admins only: the shared X-API-Key gets
 * 403 `admin_jwt_required`, 422 `cannot_deactivate_self` for one's own
 * account. Returns `{ id, status, changed, auditId }` — `changed: false`
 * when the account was already in that state. There is no delete.
 */
export async function setUserActive(userId, active, reason) {
  const { data } = await adminApi.post('/admin-users/set-active', { userId, active, reason })
  return data
}

/**
 * range: { from?, to? } as Gregorian `YYYY-MM-DD` (Tehran calendar days,
 * inclusive, default the last 30, at most 366 — 422 `invalid_report_range`).
 * Returns `{ from, to, timeZone, totals, days, weeks }`: one zero-filled
 * `days` row per day (DAU, signups, attempts, failedRate 0–1, averageScore),
 * `weeks` per Saturday-start week with activity (WAU).
 */
export async function getReportSummary(range = {}) {
  const { data } = await adminApi.get('/admin-reports/summary', { params: range })
  return data
}

/**
 * tooti#316 — the «مصرف پیامک» tab: the last twelve billing months for the
 * picker, newest first (`calendar` `shamsi` → `1405-07`, `gregorian` →
 * `2026-10`; both bounded at Tehran midnight).
 */
export async function listSmsUsageMonths(calendar) {
  const { data } = await adminApi.get('/admin-sms-usage/months', { params: { calendar } })
  return data.months
}

/**
 * One month's statement: `{ key, calendar, start, end, rialPerUnit,
 * smsIr: { lines, total, amountRial }, vonage: { lines, total } }`. Cost
 * units and `amountRial` are decimal strings (exact money, never floats);
 * `amountRial` is null while AppSetting `sms.smsirUnitPriceRial` is unset.
 */
export async function getSmsUsageStatement(month, calendar) {
  const { data } = await adminApi.get('/admin-sms-usage/statement', { params: { month, calendar } })
  return data
}

/**
 * The finance-period email (25th → 24th): `{ key, sentAt, resentAt[], to[] }`
 * — whether that period's statement already went to the finance mailbox.
 */
export async function getSmsStatementEmailStatus(month) {
  const { data } = await adminApi.get('/admin-sms-usage/email-status', { params: { month } })
  return data
}

/**
 * Send that period's statement email now. Without `resend` it is the 25th's
 * own once-only send (`already-done` if it went out); `resend: true` sends
 * again. Answers `{ outcome, run?, summary? }`; `idle` + `summary.reason`
 * (`no_recipients` / `mail_not_configured`) means nothing was sent.
 */
export async function sendSmsStatementEmail(month, { resend = false } = {}) {
  const { data } = await adminApi.post('/admin-sms-usage/send-email', { month, resend })
  return data
}

/** The same statement as a CSV Blob (UTF-8 with BOM) for the accountant. */
export async function exportSmsUsageCsv(month, calendar) {
  const { data } = await adminApi.get('/admin-sms-usage/export', { params: { month, calendar }, responseType: 'blob' })
  return data
}

/**
 * V3-p — the nine onboarding sample slots (level × position 0–2) for the
 * «نمونه‌های آنبوردینگ» tab: `[{ level, position, sample | null }]`, always
 * nine rows in beginner → advanced order, `sample` null while the slot is
 * empty. A sample is `{ level, position, audioUrl, audioKey, textEn,
 * durationMs, updatedAt }`; `audioUrl` is public, the same one learners get.
 */
export async function listOnboardingSamples() {
  const { data } = await adminApi.get('/admin-onboarding/list')
  return data.items
}

/**
 * form: { level, position, textEn, file } — multipart. Same audio checks as
 * sentence reference audio (422 `audio_rejected` / `audio_unreadable`);
 * replaces whatever was in that slot. Returns the stored sample.
 */
export async function uploadOnboardingSample({ level, position, textEn, file }) {
  const form = new FormData()
  form.append('level', level)
  form.append('position', String(position))
  form.append('textEn', textEn)
  form.append('file', file)
  const { data } = await adminApi.post('/admin-onboarding/upload-sample', form)
  return data
}

/**
 * V7-p — the Arcade's admin side (V7-b routes). `level` is one of
 * beginner/intermediate/advanced, or omitted for all three. Returns
 * `{ items, unassignedLessons }`: every stage of the level(s) in path order
 * and any status, each with its lessons (`orderInStage`, then the flat
 * `displayOrder`, as the tie-break), plus that level's non-archived lessons
 * that sit in no stage at all — a published one among those is invisible on
 * the learner's path, which is what the tab warns about.
 */
export async function listStages(level) {
  const { data } = await adminApi.get('/admin-arcade/list-stages', { params: level ? { level } : {} })
  return data
}

/**
 * dto: { level, titleFa, titleEn, theme?, status?, order? } — appended after
 * the level's last stage unless `order` names a free slot (422
 * `stage_order_taken`; the panel always appends and reorders afterwards).
 * `status` defaults to draft, and only a published stage is on the path.
 */
export async function createStage(dto) {
  const { data } = await adminApi.post('/admin-arcade/create-stage', dto)
  return data
}

/**
 * patch: { titleFa?, titleEn?, theme?, status? } — omitted fields are left
 * alone, `theme: null` clears it. A stage's level never changes (move its
 * lessons instead) and its position goes through reorderStages, not here.
 */
export async function updateStage(stageId, patch) {
  const { data } = await adminApi.post('/admin-arcade/update-stage', { stageId, ...patch })
  return data
}

/**
 * The whole level in one call: `stageIds` must be exactly that level's
 * stages, each once, in the new order — anything else is 422
 * `stage_set_mismatch`. Positions become 0..n-1. Returns the level's
 * refreshed `{ items, unassignedLessons }`.
 */
export async function reorderStages(level, stageIds) {
  const { data } = await adminApi.post('/admin-arcade/reorder-stages', { level, stageIds })
  return data
}

/**
 * Put a lesson in a stage of its own level (422 `level_mismatch` otherwise)
 * at `orderInStage`, or take it out of the Arcade with `stageId: null`.
 * Omitting `orderInStage` appends after the stage's last lesson. Positions
 * need not be unique — ties fall back to `displayOrder` — so the tab
 * renumbers a stage's lessons itself rather than trusting a single move.
 * The flat catalogue order is never touched. Returns the updated lesson row.
 */
export async function assignLesson({ lessonId, stageId, orderInStage }) {
  const body = { lessonId, stageId }
  if (orderInStage !== undefined && stageId !== null) body.orderInStage = orderInStage
  const { data } = await adminApi.post('/admin-arcade/assign-lesson', body)
  return data
}

// ─── The shared dictionary (admin-dictionary, V2-p) ──────────────────────────

/**
 * filters: { q?, status?, limit?, offset? } — `q` is a case-insensitive
 * substring of the headword or its normalized form; `status` one of
 * `draft` · `needs_review` · `published`. Answers `{ items, total }`,
 * ordered by normalized form. The word picker calls this with a small limit
 * while the admin types.
 */
export async function listDictionary(filters = {}) {
  const params = {}
  for (const key of ['q', 'status', 'limit', 'offset']) {
    if (filters[key] !== undefined && filters[key] !== '' && filters[key] !== null) params[key] = filters[key]
  }
  const { data } = await adminApi.get('/admin-dictionary/list', { params })
  return data
}

export async function getDictionaryEntry(entryId) {
  const { data } = await adminApi.get(`/admin-dictionary/detail/${entryId}`)
  return data
}

/** dto: { headword, meaningFa?, translationFa?, explanationFa?, examples?: [{ textEn, textFa }], status? } */
export async function createDictionaryEntry(dto) {
  const { data } = await adminApi.post('/admin-dictionary/create', dto)
  return data
}

/**
 * patch: any of headword, meaningFa, translationFa, explanationFa (null
 * clears), examples (replaces the whole list), status. The server answers
 * 409 `entry_in_use` when a linked entry would move to another word and
 * 422 `entry_incomplete` when publishing with no Persian content —
 * `dictionaryRefusal` (pages/admin/dictionary.js) turns both into Persian.
 */
export async function updateDictionaryEntry(entryId, patch) {
  const { data } = await adminApi.post('/admin-dictionary/update', { id: entryId, ...patch })
  return data
}

export async function uploadDictionaryAudio(entryId, file) {
  const form = new FormData()
  form.append('entryId', entryId)
  form.append('file', file)
  const { data } = await adminApi.post('/admin-dictionary/upload-audio', form)
  return data
}

// ─── Teams (admin-teams, V4-p) ───────────────────────────────────────────────

/**
 * filters: { periodKey?, level? } — `periodKey` a shamsi month `YYYY-MM`
 * (default: the current one, Tehran). Answers `{ periodKey, maxSize, items }`
 * with each team's `memberCount`, in `position` order. Works whether or not
 * `teams.enabled` is on.
 */
export async function listTeams(filters = {}) {
  const params = {}
  for (const key of ['periodKey', 'level']) {
    if (filters[key]) params[key] = filters[key]
  }
  const { data } = await adminApi.get('/admin-teams/list', { params })
  return data
}

/** One team and its members (XP, streak, last practice, how they joined). 404 `team_not_found`. */
export async function getTeam(teamId) {
  const { data } = await adminApi.get(`/admin-teams/detail/${teamId}`)
  return data
}

/**
 * Seat a learner in `toTeamId` for that team's period. JWT-signed-in admins
 * only (403 `admin_jwt_required`); 409 `team_full` at `teams.maxSize`, 404
 * `team_not_found` / `user_not_found`. Written to `admin_audit_log` with
 * the optional `reason`. Returns the target team's detail.
 */
export async function moveTeamMember({ userId, toTeamId, reason }) {
  const body = { userId, toTeamId }
  if (reason) body.reason = reason
  const { data } = await adminApi.post('/admin-teams/move', body)
  return data
}

/**
 * Build the teams of a period — the monthly job. `dryRun: true` returns
 * `{ outcome: 'dry-run', summary }` (with `plannedTeams`) and writes nothing;
 * a real run answers `ran` / `already-done` with `run.summary`, or
 * `already-running`.
 */
export async function reshuffleTeams({ periodKey, dryRun }) {
  const body = { dryRun: Boolean(dryRun) }
  if (periodKey) body.periodKey = periodKey
  const { data } = await adminApi.post('/admin-teams/reshuffle', body)
  return data
}

// ─── Rewards settlement (admin-rewards, V6-p) ───────────────────────────────

/**
 * Settle a week or shamsi month (V6-b-settle). `periodType` `week` | `month`;
 * `periodKey` `W` + its Saturday (`W2026-09-19`) or `YYYY-MM` (`1405-06`),
 * omitted = the period of that type that closed last. `dryRun: true` answers
 * `{ outcome: 'dry-run', summary }` and writes nothing; a real run answers
 * `ran` / `already-done` with `run.summary`, `already-running`, or `idle`
 * with `summary.disabled` while `leaderboard.settleEnabled` / `gems.enabled`
 * is off (nothing written). 422 `period_key_invalid` / `period_not_over`.
 */
export async function settleRewards({ periodType, periodKey, dryRun }) {
  const body = { periodType, dryRun: Boolean(dryRun) }
  if (periodKey) body.periodKey = periodKey
  const { data } = await adminApi.post('/admin-rewards/settle', body)
  return data
}

/** Recent `periodic_runs` of one job (`admin-jobs_list`), newest first, at most 50: `{ items }`. */
export async function listJobRuns(jobKey) {
  const params = jobKey ? { jobKey } : {}
  const { data } = await adminApi.get('/admin-jobs/list', { params })
  return data
}

export { errorMessage }
